import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert, Button, Field, Input } from "../components/ui";
import { ApiError } from "../lib/api";
import { formatMoney } from "../lib/money";
import { absoluteDay } from "../lib/format";
import { useRedeemPromo } from "../lib/queries";
import type { PromoRedemption } from "../lib/types";

/**
 * Promotion code entry, inside the top-up dialog.
 *
 * It was a card of its own on the billing page. Moved here because that is where
 * a customer thinking about credit is looking, and a standalone card for
 * something most accounts never use is a permanent section earning its space
 * from a minority.
 *
 * Applying is independent of paying, which is what makes this placement work for
 * both kinds of campaign: a grant credits immediately and the dialog can be
 * closed without going near PayPal, while a match records itself against the
 * payment about to be started. The one cost is discoverability — somebody handed
 * a "$300 free" code has to open **Add credit** to enter it — which is the trade
 * this placement accepts.
 */
export function PromoCodeField({ orgId, currency }: { orgId: string; currency: string }) {
  const { t } = useTranslation("billing");
  const redeem = useRedeemPromo(orgId);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<PromoRedemption | null>(null);

  async function submit() {
    const trimmed = code.trim();
    if (!trimmed) return;

    setError(null);
    try {
      setApplied(await redeem.mutateAsync({ code: trimmed }));
      setCode("");
    } catch (cause) {
      // The API's own words. Every refusal has a distinct reason, and collapsing
      // them into "invalid code" would send somebody to support for five
      // different situations.
      setError(cause instanceof ApiError ? cause.message : t("promo.failed"));
    }
  }

  return (
    <div className="space-y-3 border-t border-line pt-4">
      {error && <Alert>{error}</Alert>}

      {applied && (
        <Alert tone="success">
          {applied.creditedMicros > 0 ? (
            <>
              <Trans
                ns="billing"
                i18nKey="promo.credited"
                values={{ amount: formatMoney(applied.creditedMicros, currency) }}
                components={{ amount: <span className="font-medium" /> }}
              />{" "}
              {applied.expiresAt
                ? t("promo.expires", { date: absoluteDay(applied.expiresAt) })
                : ""}
              {t("promo.workloadsOnly")}
            </>
          ) : applied.pending ? (
            <Trans
              ns="billing"
              i18nKey="promo.match"
              values={{
                percent: applied.pending.matchBps / 100,
                cap: formatMoney(applied.pending.capMicros, currency),
              }}
              components={{ b: <span className="font-medium" /> }}
            />
          ) : (
            <Trans ns="billing" i18nKey="promo.applied" components={{ b: <span className="font-medium" /> }} />
          )}
        </Alert>
      )}

      {/*
        Input and button on one row *inside* the field, so the label spans both.
        Laying them out as siblings and reaching for `items-end` aligned the
        button to the bottom of the whole field — which is the bottom of the
        hint, not the input — so Apply sat a row low. `Field` stacks label,
        children, then hint, and cross-aligning against that column never works.

        No hint: it was "Optional.", which is what pushed the button down and
        what an empty field beside an Apply button already says.
      */}
      <Field label={t("promo.label")} htmlFor="promo-code">
        <div className="flex gap-2">
          {/* `min-w-0` so a long code shrinks the input rather than the button. */}
          <div className="min-w-0 flex-1">
            <Input
              id="promo-code"
              value={code}
              placeholder="TRY300"
              autoComplete="off"
              className="font-mono uppercase"
              onChange={(event) => setCode(event.target.value)}
              onKeyDown={(event) => {
                // Enter applies the code rather than submitting the dialog —
                // otherwise typing a code and pressing Enter starts a payment.
                if (event.key === "Enter") {
                  event.preventDefault();
                  void submit();
                }
              }}
            />
          </div>
          <Button
            variant="outline"
            className="shrink-0"
            loading={redeem.isPending}
            disabled={code.trim().length === 0}
            onClick={() => void submit()}
          >
            {t("promo.apply")}
          </Button>
        </div>
      </Field>
    </div>
  );
}
